import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';

const ProfilePage: React.FC = () => {
  const { user } = useAuth();

  // Profile fields
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMessage, setNameMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMessage, setPwMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Sync if user object updates (e.g. after re-fetch)
  useEffect(() => {
    if (user?.full_name) setFullName(user.full_name);
  }, [user?.full_name]);

  const handleSaveName = async () => {
    if (!user?.id || !fullName.trim()) return;
    setNameSaving(true);
    setNameMessage(null);
    try {
      await apiClient.patch('/v1/users/' + user.id, { full_name: fullName.trim() });
      setNameMessage({ type: 'success', text: 'Name updated.' });
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to update name.';
      setNameMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Failed to update name.' });
    } finally {
      setNameSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user?.id) return;
    setPwMessage(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwMessage({ type: 'error', text: 'All password fields are required.' });
      return;
    }
    if (newPassword.length < 8) {
      setPwMessage({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }

    setPwSaving(true);
    try {
      await apiClient.post('/v1/users/' + user.id + '/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPwMessage({ type: 'success', text: 'Password changed successfully.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to change password.';
      setPwMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Failed to change password.' });
    } finally {
      setPwSaving(false);
    }
  };

  const roleBadgeColor = (role?: string) => {
    switch (role) {
      case 'admin': return { bg: '#dc26261a', text: '#dc2626' };
      case 'manager': return { bg: '#2563eb1a', text: '#2563eb' };
      default: return { bg: 'var(--primary-600)', text: '#fff' };
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64" style={{ color: 'var(--text-muted)' }}>
        Loading profile...
      </div>
    );
  }

  const badge = roleBadgeColor(user.role);

  return (
    <div className="max-w-xl mx-auto py-12 px-4">
      <h1 className="text-2xl font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>
        Profile
      </h1>

      {/* --- Profile Info Card --- */}
      <div
        className="rounded-lg border p-6 mb-6"
        style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}
      >
        {/* Full Name (editable) */}
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
          Full Name
        </label>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="flex-1 rounded-md border px-3 py-2 text-sm outline-none focus:ring-1"
            style={{
              background: 'var(--input-bg)',
              borderColor: 'var(--input-border)',
              color: 'var(--text-primary)',
            }}
          />
          <button
            onClick={handleSaveName}
            disabled={nameSaving || fullName.trim() === (user.full_name || '')}
            className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            style={{ background: 'var(--primary-600)' }}
          >
            {nameSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
        {nameMessage && (
          <p className="text-sm mb-4" style={{ color: nameMessage.type === 'success' ? '#16a34a' : '#dc2626' }}>
            {nameMessage.text}
          </p>
        )}

        {/* Email (read-only) */}
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
          Email
        </label>
        <div
          className="rounded-md border px-3 py-2 text-sm mb-4"
          style={{
            background: 'var(--input-bg)',
            borderColor: 'var(--input-border)',
            color: 'var(--text-muted)',
            opacity: 0.75,
          }}
        >
          {user.email || '--'}
        </div>

        {/* Username (read-only) */}
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
          Username
        </label>
        <div
          className="rounded-md border px-3 py-2 text-sm mb-4"
          style={{
            background: 'var(--input-bg)',
            borderColor: 'var(--input-border)',
            color: 'var(--text-muted)',
            opacity: 0.75,
          }}
        >
          {user.username || '--'}
        </div>

        {/* Role badge */}
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
          Role
        </label>
        <span
          className="inline-block rounded-full px-3 py-1 text-xs font-medium"
          style={{ background: badge.bg, color: badge.text }}
        >
          {user.role || 'member'}
        </span>
      </div>

      {/* --- Change Password Card --- */}
      <div
        className="rounded-lg border p-6"
        style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}
      >
        <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
          Change Password
        </h2>

        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
          Current Password
        </label>
        <input
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm mb-3 outline-none focus:ring-1"
          style={{
            background: 'var(--input-bg)',
            borderColor: 'var(--input-border)',
            color: 'var(--text-primary)',
          }}
        />

        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
          New Password
        </label>
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm mb-3 outline-none focus:ring-1"
          style={{
            background: 'var(--input-bg)',
            borderColor: 'var(--input-border)',
            color: 'var(--text-primary)',
          }}
        />

        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
          Confirm New Password
        </label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm mb-4 outline-none focus:ring-1"
          style={{
            background: 'var(--input-bg)',
            borderColor: 'var(--input-border)',
            color: 'var(--text-primary)',
          }}
        />

        {pwMessage && (
          <p className="text-sm mb-3" style={{ color: pwMessage.type === 'success' ? '#16a34a' : '#dc2626' }}>
            {pwMessage.text}
          </p>
        )}

        <button
          onClick={handleChangePassword}
          disabled={pwSaving || !currentPassword || !newPassword || !confirmPassword}
          className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          style={{ background: 'var(--primary-600)' }}
        >
          {pwSaving ? 'Changing...' : 'Change Password'}
        </button>
      </div>
    </div>
  );
};

export default ProfilePage;
